// ============================================================
// SettlementWordingPanel.tsx — B4: Admin → Settlement wording (admins only;
// backend settlementWordingController). Like Bill wording:
//   • the plain-English explanation for each deduction (and the "already paid
//     or held back" lines), the client-agreement block, the child / protected-
//     party wording — each with "Use Casevo's default";
//   • the agreement's statement date / total deductions go in from the
//     "Insert" menu (shown as [Statement date] — never typed as tokens);
//   • (B4 follow-up) the statement's OWN "Your rights" paragraph — it speaks of
//     "the charges shown in this statement" / "our bill" (firm details from
//     the Insert menu, as Bill wording);
//   • the complaints wording + SRA footer are the BILL's — edit them in Bill
//     wording; shown here as printed;
//   • approve once nothing is missing — the approval names exactly the text
//     previewed; any edit (here, or to the bill notices / firm details) makes
//     it DRAFT again. Statements show a DRAFT banner and can't issue until then.
// ============================================================
import ActionBox, { ErrorNote } from '../shared/ActionBox'
import React, { useEffect, useRef, useState } from 'react'
import { SettingsCard, S_BTN_PRIMARY, S_BTN_GHOST, S_BTN_GHOST_SM, S_LABEL, S_NOTE, S_FORMFOOT, S_FIELD_ERROR, sInput, SettingsSaved } from './settingsUi'
import { InsertMenu, insertAt, wordingStatusText, wordingToDisplay, wordingToTokens } from './BillWordingPanel'
import { approveSettlementWordingApi, getSettlementWordingApi, patchSettlementWordingApi, unapproveSettlementWordingApi } from '../../api/casevoApi'
import type { ExplanationKey, SettlementWordingPatch, SettlementWordingState } from '../../types/settlementStatement.types'
import { serverErrorMessage } from '../../utils/serverError'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import { UnsavedGuard } from '../../hooks/useUnsavedChangesGuard'
// (Batch 2) "Withdraw approval" asks first (ConfirmDialogue); unsaved edits
// are guarded — leaving / navigating away asks first (M16).

type Form = { explanations: Record<string, string>; agreement: string; child: string; rights: string }
const formOf = (d: SettlementWordingState): Form => ({
  explanations: Object.fromEntries(d.labels.map((l) => [l.key, d.explanations[l.key] ?? d.defaults.explanations[l.key]])),
  agreement: wordingToDisplay(d.agreement ?? d.defaults.agreement, d.placeholders),
  child: d.child ?? d.defaults.child,
  rights: wordingToDisplay(d.rights ?? d.defaults.rights, d.rightsPlaceholders),
})
// Only what differs from the saved state (a text equal to the default → null).
export const wordingPatchOf = (d: SettlementWordingState, f: Form): SettlementWordingPatch => {
  const patch: SettlementWordingPatch = {}
  const ex: Partial<Record<ExplanationKey, string | null>> = {}
  for (const { key } of d.labels) {
    const now = f.explanations[key].trim()
    const saved = d.explanations[key] ?? d.defaults.explanations[key]
    if (now !== saved.trim()) ex[key] = now === d.defaults.explanations[key].trim() ? null : now
  }
  if (Object.keys(ex).length) patch.explanations = ex
  const agreement = wordingToTokens(f.agreement.trim(), d.placeholders)
  if (agreement !== (d.agreement ?? d.defaults.agreement).trim()) patch.agreement = agreement === d.defaults.agreement.trim() ? null : agreement
  const child = f.child.trim()
  if (child !== (d.child ?? d.defaults.child).trim()) patch.child = child === d.defaults.child.trim() ? null : child
  const rights = wordingToTokens(f.rights.trim(), d.rightsPlaceholders)
  if (rights !== (d.rights ?? d.defaults.rights).trim()) patch.rights = rights === d.defaults.rights.trim() ? null : rights
  return patch
}

const SettlementWordingPanel: React.FC<{ onOpenBillWording?: () => void }> = ({ onOpenBillWording }) => {
  const [data, setData] = useState<SettlementWordingState | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [confirmWithdraw, setConfirmWithdraw] = useState(false)
  const agreementRef = useRef<HTMLTextAreaElement | null>(null)
  const rightsRef = useRef<HTMLTextAreaElement | null>(null)
  const seed = (d: SettlementWordingState): void => { setData(d); setForm(formOf(d)) }
  useEffect(() => {
    getSettlementWordingApi().then(seed).catch((e) => setLoadErr(serverErrorMessage(e, [400, 403, 404, 500]) ?? 'Couldn’t load the settlement wording.'))
  }, [])
  if (loadErr) return <ErrorNote>{loadErr}</ErrorNote>
  if (!data || !form) return <p className={S_NOTE}>Loading the settlement wording…</p>
  const patch = wordingPatchOf(data, form)
  const dirty = Object.keys(patch).length > 0

  const save = async (): Promise<void> => {
    setSaving(true); setError(null); setFieldErr({})
    try { seed(await patchSettlementWordingApi(patch)); setSaved(true) } catch (e) {
      const r = (e as { response?: { data?: { field?: string; error?: string } } }).response?.data
      if (r?.field && r.error) setFieldErr({ [r.field]: r.error })
      else setError(serverErrorMessage(e, [400, 409]) ?? 'Couldn’t save the wording.')
    } finally { setSaving(false) }
  }
  const approve = async (yes: boolean): Promise<void> => {
    setBusy(true); setError(null)
    try { seed(yes ? await approveSettlementWordingApi(data.previewHash) : await unapproveSettlementWordingApi()) } catch (e) {
      setError(serverErrorMessage(e, [400, 409]) ?? 'Couldn’t change the approval.')
      const fresh = (e as { response?: { status?: number; data?: { data?: SettlementWordingState } } })?.response
      if (fresh?.status === 409 && fresh.data?.data) seed(fresh.data.data)
    } finally { setBusy(false) }
  }
  const setText = (k: 'agreement' | 'child' | 'rights', v: string): void => { setForm((f) => (f ? { ...f, [k]: v } : f)); setSaved(false) }
  const setExplanation = (k: string, v: string): void => { setForm((f) => (f ? { ...f, explanations: { ...f.explanations, [k]: v } } : f)); setSaved(false) }

  return (
    <div className="space-y-[18px]">
      <SettingsCard icon="file" title="Settlement wording" sub="The wording on every settlement / completion statement. All Casevo default text is DRAFT and must be reviewed by a solicitor at your firm.">
        {/* (Audit fix batch 2) Green = approved; red = not approved. */}
        <ActionBox live tone={data.approved ? 'done' : 'danger'} title={wordingStatusText(data)} message={data.approved ? undefined : 'Statements can be drafted and previewed, but can’t be issued or sent to a client until a solicitor at your firm approves this wording.'} />
        {data.problems.length > 0 && (
          <div className="mt-[12px] text-[12.5px] text-casevo-forest">
            <b className="font-semibold">To fill in before it can be approved:</b>
            <ul className="mt-[4px] mb-0 pl-[18px] list-disc space-y-[2px]">{data.problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </div>
        )}
        <div className={S_FORMFOOT}>
          {data.approved
            ? <button type="button" className={S_BTN_GHOST} disabled={busy} onClick={() => setConfirmWithdraw(true)}>Withdraw approval</button>
            : <button type="button" className={S_BTN_PRIMARY} disabled={busy || dirty || data.problems.length > 0} onClick={() => { void approve(true) }}
                title={dirty ? 'Save your changes first' : data.problems.length ? 'Fill in what’s missing first' : undefined}>Approve wording</button>}
          {!data.approved && dirty && <span className={S_NOTE}>Save your changes before approving.</span>}
        </div>
      </SettingsCard>

      <SettingsCard icon="pen" title="What each deduction means" sub="One plain-English sentence under each line of the statement, so the client understands what is taken and why.">
        {data.labels.map(({ key, label }) => (
          <div key={key} className="mt-[14px]">
            <div className="flex items-center gap-[8px]">
              <label htmlFor={`sw-${key}`} className={`${S_LABEL} mb-0`}>{label}</label>
              {form.explanations[key].trim() !== data.defaults.explanations[key].trim() && (
                <button type="button" className={`${S_BTN_GHOST_SM} ml-auto`} onClick={() => setExplanation(key, data.defaults.explanations[key])}>Use Casevo’s default</button>
              )}
            </div>
            <textarea id={`sw-${key}`} rows={2} value={form.explanations[key]} onChange={(e) => setExplanation(key, e.target.value)}
              aria-invalid={fieldErr[`explanations.${key}`] ? true : undefined}
              className={`${sInput(!!fieldErr[`explanations.${key}`])} !h-auto py-[8px] mt-[6px] resize-y leading-[1.5]`} />
            {fieldErr[`explanations.${key}`] && <p className={S_FIELD_ERROR}>{fieldErr[`explanations.${key}`]}</p>}
          </div>
        ))}
      </SettingsCard>

      <SettingsCard icon="check" title="The client’s agreement" sub="Printed on an adult’s statement. The statement date and total deductions are filled in for each statement.">
        <div className="flex items-center gap-[8px] mt-[6px]">
          <label htmlFor="sw-agreement" className={`${S_LABEL} mb-0`}>Agreement wording</label>
          <div className="ml-auto flex items-center gap-[6px]">
            <InsertMenu label="Agreement wording" placeholders={data.placeholders} onPick={(l) => {
              const el = agreementRef.current
              const r = insertAt(form.agreement, `[${l}]`, el ? el.selectionStart : null, el ? el.selectionEnd : null)
              setText('agreement', r.text)
              requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(r.caret, r.caret) } })
            }} />
            {wordingToTokens(form.agreement.trim(), data.placeholders) !== data.defaults.agreement.trim() && (
              <button type="button" className={S_BTN_GHOST_SM} onClick={() => setText('agreement', wordingToDisplay(data.defaults.agreement, data.placeholders))}>Use Casevo’s default</button>
            )}
          </div>
        </div>
        <textarea id="sw-agreement" ref={agreementRef} rows={6} value={form.agreement} onChange={(e) => setText('agreement', e.target.value)}
          aria-invalid={fieldErr.agreement ? true : undefined} aria-describedby="sw-agreement-preview"
          className={`${sInput(!!fieldErr.agreement)} !h-auto py-[8px] mt-[6px] resize-y leading-[1.5]`} />
        {fieldErr.agreement && <p className={S_FIELD_ERROR}>{fieldErr.agreement}</p>}
        <p id="sw-agreement-preview" className={`${S_NOTE} mt-[6px]`}><b className="font-semibold text-casevo-forest">As printed (saved version, example figures):</b> {data.preview.agreementSample}</p>
      </SettingsCard>

      <SettingsCard icon="users" title="Children and protected parties" sub="Printed instead of the agreement block: the court approves the deductions, not the client.">
        <div className="flex items-center gap-[8px] mt-[6px]">
          <label htmlFor="sw-child" className={`${S_LABEL} mb-0`}>Child / protected-party wording</label>
          {form.child.trim() !== data.defaults.child.trim() && (
            <button type="button" className={`${S_BTN_GHOST_SM} ml-auto`} onClick={() => setText('child', data.defaults.child)}>Use Casevo’s default</button>
          )}
        </div>
        <textarea id="sw-child" rows={4} value={form.child} onChange={(e) => setText('child', e.target.value)} aria-invalid={fieldErr.child ? true : undefined}
          className={`${sInput(!!fieldErr.child)} !h-auto py-[8px] mt-[6px] resize-y leading-[1.5]`} />
        {fieldErr.child && <p className={S_FIELD_ERROR}>{fieldErr.child}</p>}
      </SettingsCard>

      <SettingsCard icon="shield" title="Your rights" sub="The statement’s own rights paragraph (section 70 of the Solicitors Act 1974). It speaks of the charges shown in the statement and your bill.">
        <div className="flex items-center gap-[8px] mt-[6px]">
          <label htmlFor="sw-rights" className={`${S_LABEL} mb-0`}>Your rights</label>
          <div className="ml-auto flex items-center gap-[6px]">
            <InsertMenu label="Your rights" placeholders={data.rightsPlaceholders} onPick={(l) => {
              const el = rightsRef.current
              const r = insertAt(form.rights, `[${l}]`, el ? el.selectionStart : null, el ? el.selectionEnd : null)
              setText('rights', r.text)
              requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(r.caret, r.caret) } })
            }} />
            {wordingToTokens(form.rights.trim(), data.rightsPlaceholders) !== data.defaults.rights.trim() && (
              <button type="button" className={S_BTN_GHOST_SM} onClick={() => setText('rights', wordingToDisplay(data.defaults.rights, data.rightsPlaceholders))}>Use Casevo’s default</button>
            )}
          </div>
        </div>
        <textarea id="sw-rights" ref={rightsRef} rows={6} value={form.rights} onChange={(e) => setText('rights', e.target.value)}
          aria-invalid={fieldErr.rights ? true : undefined} aria-describedby="sw-rights-preview"
          className={`${sInput(!!fieldErr.rights)} !h-auto py-[8px] mt-[6px] resize-y leading-[1.5]`} />
        {fieldErr.rights && <p className={S_FIELD_ERROR}>{fieldErr.rights}</p>}
        <p id="sw-rights-preview" className={`${S_NOTE} mt-[6px]`}><b className="font-semibold text-casevo-forest">As printed (saved version):</b> {data.preview.rights}</p>
      </SettingsCard>

      <SettingsCard icon="mail" title="Complaints and SRA footer (from Bill wording)" sub="The same as on your bills. Changing them there makes this wording DRAFT again.">
        <dl className="m-0 mt-[6px] space-y-[8px] text-[12.5px]">
          <div><dt className={S_LABEL}>Complaints</dt><dd className="m-0">{data.preview.complaints}</dd></div>
          <div><dt className={S_LABEL}>SRA footer</dt><dd className="m-0">{data.preview.sraFooter}</dd></div>
        </dl>
        {onOpenBillWording && <div className={S_FORMFOOT}><button type="button" className={S_BTN_GHOST} onClick={onOpenBillWording}>Edit in Bill wording</button></div>}
      </SettingsCard>

      {error && <ErrorNote className="mt-[12px]">{error}</ErrorNote>}
      <div className={S_FORMFOOT}>
        <button type="button" className={S_BTN_PRIMARY} disabled={!dirty || saving} onClick={() => { void save() }}>{saving ? 'Saving…' : 'Save wording'}</button>
        {dirty && data.approved && <span className={S_NOTE}>Saving makes the wording DRAFT again until it’s re-approved.</span>}
        {saved && !dirty && <SettingsSaved />}
        <UnsavedGuard dirty={dirty && !saving} />
      </div>
      <ConfirmDialogue isOpen={confirmWithdraw} title="Withdraw approval of the settlement wording?"
        message="The wording goes back to DRAFT. Settlement statements can’t be issued or sent to a client until a solicitor at your firm approves it again."
        confirmLabel="Withdraw approval" tone="danger" onCancel={() => setConfirmWithdraw(false)} onConfirm={() => { setConfirmWithdraw(false); void approve(false) }} />
    </div>
  )
}

export default SettlementWordingPanel
